"use client";
import ChipCustom from "@/components/Chip/ChipCustom";
import { DividerCustom } from "@/components/Divider/DividerCustom";
import IconLoader from "@/components/IconLoader";
import HeaderTextWithIcons from "@/components/Paragraph/HeaderTextWithIcons";
import TextLink, { ITextLinkProps } from "@/components/Paragraph/TextLink";
import WithLazy from "@/components/WithLazy";
import { useTranslation } from "@/i18n/client";
import { ContentManagementQueryKeyEnum } from "@/libs/enum/content-management-query-key.enum";
import { IncreaseContentManagementQueryMap } from "@/libs/services/viewCount/type";
import { ViewCountService } from "@/libs/services/viewCount/view-count.service";
import { ContentManagementListResponse } from "@/libs/types/content-management.type";
import color from "@/styles/color";
import { Box, Container, Skeleton, SxProps, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import NextDynamic from "next/dynamic";
import { useEffect, useState } from "react";
import "../../styles/show-image-video-best-solution.css";
import { ArticleSearchHistoryStore } from "@/contexts/useSearchArticle";
import Link from "next/link";
import { ENV_CONFIG } from "@/libs";
import { useReadScreen } from "@/hooks/useReadScreen.hook";

const ArticleRelate = NextDynamic(() => import("@/components/Paragraph/ArticleRelate"), {
  ssr: false,
  loading: () => (
    <Skeleton
      key={`article-relate-details-skeleton`}
      variant="rectangular"
      sx={{
        width: "100%",
        height: "550px",
        padding: { xs: "24px", sm: "40px 48px 28px 48px", lg: "40px 48px 28px 48px" },
      }}
    />
  ),
});

interface contentIdType {
  contentId: string;
  articleData?: ContentManagementListResponse;
  articleDataRelateInit?: ContentManagementListResponse;
  referenceCode?: ContentManagementQueryKeyEnum;
}

const style: SxProps = {
  fontWeight: { xs: 400, sm: 400, lg: 400 },
  fontSize: { xs: "15px", sm: "15px", lg: "15px" },
  lineHeight: { xs: "24px", sm: "24px", lg: "24px" },
  color: "#A1A1A1",
};

const dataTextLink: ITextLinkProps[][] = [
  [
    {
      name: "Article",
      href: "",
    },
    {
      name: "knowTip",
      href: "/home/knowledge",
      canBackWard: false,
    },
  ],
  [
    {
      name: "Article",
      href: "",
    },
    {
      name: "news",
      href: "/home/news",
      canBackWard: false,
    },
  ],
  [
    {
      name: "Article",
      href: "",
    },
    {
      name: "promotion",
      href: "/promotions/promotionlist",
      canBackWard: false,
    },
  ],
];

const ContentDetailPage = ({
  contentId,
  articleData,
  articleDataRelateInit,
  referenceCode,
}: contentIdType) => {
  const baseUrl = ENV_CONFIG.MAIN_URL;
  const { knowHowAndTip, news, promotion } = ArticleSearchHistoryStore();

  if (knowHowAndTip) {
    dataTextLink[0][1].canBackWard = true;
  }
  if (news) {
    dataTextLink[1][1].canBackWard = true;
  }
  if (promotion) {
    dataTextLink[2][1].canBackWard = true;
  }
  const { t } = useTranslation();
  const { isLessThanSm } = useReadScreen();

  const [articleRelate, setArticleRelate] = useState<ContentManagementListResponse>();

  let dataLink = dataTextLink[0];
  if (referenceCode == ContentManagementQueryKeyEnum.CUSTNEWS) {
    dataLink = dataTextLink[1];
  }
  if (
    referenceCode == ContentManagementQueryKeyEnum.ALLPROM
    // referenceCode == ContentManagementQueryKeyEnum.TMTPROM ||
    // referenceCode == ContentManagementQueryKeyEnum.DLRPROM ||
    // referenceCode == ContentManagementQueryKeyEnum.FINPROM
  ) {
    dataLink = dataTextLink[2];
  }

  const linkHref = `/contentdetail/contentdetailsui/${contentId.toLowerCase()}`;

  const { data: getViewCount } = useQuery({
    queryKey: [],
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    queryFn: () => {
      const params = new IncreaseContentManagementQueryMap();
      params.set({ ContentId: contentId });
      return ViewCountService.IncreaseContentManagement(params);
    },
  });

  useEffect(() => {
    setArticleRelate(articleDataRelateInit);
  }, []);

  const setMarginForNewsAndPromotion: SxProps | undefined = {
    margin: {
      xs: "-60px auto 24px auto",
      sm: "-120px auto 24px auto",
      lg: "-120px auto 24px auto",
    },
  };

  return (
    <Box
      component={"div"}
      sx={{
        marginTop: { xs: "56px", sm: "104px", lg: "120px" },
        minHeight: "calc(100vh - 100px)",
        background:
          "linear-gradient(180deg, rgba(255, 255, 255, 0.5) 0%, rgba(254, 230, 230, 0.5) 100%)",
      }}
    >
      {referenceCode != ContentManagementQueryKeyEnum.KNOWTIP &&
        referenceCode != ContentManagementQueryKeyEnum.TMTBAN && (
          <Box
            sx={{
              minHeight: {
                xs: "calc(100Vh - calc(100vh - 420px))",
                md: "calc(100Vh - calc(100vh - 520px))",
                lg: "calc(100Vh - calc(100vh - 620px))",
              },
              background: color.black,
              margin: { xs: "0px", sm: "120px 20px 40px 20px", lg: "120px 20px 40px 20px" },
              borderRadius: { xs: "0px", sm: "24px", lg: "24px" },
              backgroundImage: `url(${articleData?.ContentManagement[0].ImageFileURL})`,
              backgroundRepeat: "no-repeat",
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          ></Box>
        )}

      <Container
        maxWidth="2xl"
        sx={{
          margin:
            referenceCode != ContentManagementQueryKeyEnum.KNOWTIP
              ? setMarginForNewsAndPromotion
              : null,
          padding: { xs: "0px 24px", sm: "0px 48px", lg: "auto 48px" },
          minHeight: "calc(100vh - 100px)",
          width: "100%",
        }}
      >
        <Box
          sx={{
            width: "100%",
            gap: { xs: 0, sm: "28px", lg: "28px" },
            padding: { xs: "24px", sm: "40px 48px", lg: "40px 48px" },
            borderRadius: "24px",
            minHeight: "calc(100vh - 100px)",
            backgroundColor: "white",

            marginBottom: { xs: "24px", sm: "24px", lg: "24px" },
          }}
        >
          <Box>
            <TextLink data={dataLink} data2={articleData?.ContentManagement[0].Title} />
            <HeaderTextWithIcons
              header={articleData?.ContentManagement[0].Title}
              publicDate={articleData?.ContentManagement[0].CreatedOn}
              viewer={getViewCount?.ViewCountInfo[0].ViewCount}
              author={articleData?.ContentManagement[0].CustomerName}
              link={linkHref}
            />

            <WithLazy style={{ minHeight: 100 }} delay={0}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: { xs: "16px", xl: "40px" },
                  marginBottom: { xs: "24px", lg: "28px" },
                }}
              >
                <Typography
                  component={"span"}
                  sx={{
                    fontSize: { xs: "16px", md: "16px", lg: "18px" },
                    lineHeight: { xs: "27px", md: "27px", lg: "27px" },
                    overflowWrap: "anywhere",
                    overflow: "auto",

                    color: color.black300,
                  }}
                  dangerouslySetInnerHTML={{
                    __html:
                      articleData?.ContentManagement[0].Description.replace(
                        /<h1>/g,
                        "<h2 >",
                      ).replace(/<\/h1>/g, "</h2>") ?? "",
                  }}
                  className="best-solution"
                ></Typography>
              </Box>
            </WithLazy>

            <WithLazy style={{ minHeight: 100 }} delay={0}>
              <Box>
                <Typography sx={{ ...style }}>
                  <IconLoader
                    iconName="CalendarOutlineIcon"
                    sx={{ fontSize: { xs: "24px", sm: "24px" }, color: color.gray190 }}
                  />
                  {t("Article.LastUpdate")}{" "}
                  {dayjs(articleData?.ContentManagement[0].UpdatedOn).format("DD/MM/YYYY")}
                </Typography>
              </Box>

              <DividerCustom
                sx={{
                  margin: {
                    xs: "24px 0px !important",
                    sm: "24px 0px !important",
                    lg: "28px 0px !important",
                  },
                }}
              ></DividerCustom>
            </WithLazy>

            <WithLazy style={{ minHeight: 100 }} delay={0}>
              <Box sx={{ display: "flex", ...style, gap: { xs: "16px", md: "16px", lg: "16px" } }}>
                <Box>
                  <Typography sx={{ ...style, color: "#333333" }}>
                    <IconLoader
                      iconName="TagIcon"
                      sx={{
                        fontSize: { xs: "40px", sm: "40px" },
                        padding: {
                          xs: "8px 0px 0px 0px",
                          md: "8px 0px 0px 0px",
                          lg: "8px 0px 0px 0px",
                        },
                      }}
                    />
                    {"Tag : "}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: isLessThanSm ? "column" : "row",
                    gap: { xs: "0px", md: "15px" },
                  }}
                >
                  {articleData?.ContentManagement[0].Tags?.map((item, index) => (
                    <Box
                      key={index}
                      sx={{
                        padding: {
                          xs: "15px 0px 0px 0px",
                          md: "5px 0px 0px 0px",
                          lg: "5px 0px 0px 0px",
                        },
                      }}
                    >
                      <Link
                        href={`${baseUrl}/${dataLink[1].href}?searchTag=${item.Tag}`}
                        rel="noopener noreferrer"
                      >
                        <ChipCustom
                          label={item.Tag}
                          containerStyles={{
                            borderColor: "#F10503",
                            minHeight: { xs: "16px", md: "32px", lg: "32px" },
                          }}
                          labelStyles={{
                            color: "#F10503",
                            fontWeight: { xs: 400, md: 500, lg: 500 },
                            fontSize: { xs: "12px", md: "12px", lg: "12px" },
                            lineHeight: { xs: "23px", md: "23px", lg: "23px" },
                          }}
                        ></ChipCustom>
                      </Link>
                    </Box>
                  ))}
                </Box>
              </Box>
            </WithLazy>
          </Box>
        </Box>
        {!!articleDataRelateInit && (
          <WithLazy style={{ minHeight: 300 }} delay={0}>
            <Box
              sx={{
                width: "100%",
                gap: { xs: "24px", sm: "28px", lg: "28px" },

                borderRadius: "24px",
                color: "red",

                backgroundColor: "white",
                marginBottom: { sx: "24px", sm: "40px", lg: "24px" },
              }}
            >
              <ArticleRelate header={t("Article.ArticleRelated")} data={articleRelate} />
            </Box>
          </WithLazy>
        )}
      </Container>

      <div
        style={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
        }}
      ></div>
    </Box>
  );
};

export default ContentDetailPage;
